import { useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import compact from 'lodash/compact';
import uniq from 'lodash/uniq';

import { UserStatus } from 'chaika-enums';

import { OptionType } from '@components/Select';
import { store } from '@store';
import { translate } from '@chaika/i18n/helper';
import { formatFullName } from '@utils/formatFullName';
import { userQueryOptions } from '@user/queries/userQueries';

import type { TaskFieldData } from '@common/Task';
import { useTaskForm } from '@common/Task/TaskRightMenu/context/TaskFormContext';

interface Data {
    isCreationMode: boolean;
    isEditMode: boolean;
    canEdit: boolean;
    defaultUsers: OptionType<string>[];
    authorData: TaskFieldData;
    executorData: TaskFieldData[];
    onExecutorsSelect(options: OptionType<string>[]): void;
    setOnlyMe(): void;
}

export function useTaskParticipants(): Data {
    const { state, permissions, actions } = useTaskForm();
    const { authUser } = store;

    const userIds = uniq(
        compact([state.authUserId, state.authorId, state.requesterId, ...state.executorIds, state.actualExecutorId]),
    );
    const { data: users = [] } = useQuery({ ...userQueryOptions.list({ ids: userIds }), enabled: userIds.length > 0 });

    const onExecutorsSelect = (options: OptionType<string>[]): void => {
        actions.setExecutors(options.map(it => it.value));
    };

    const setOnlyMe = useCallback(() => {
        actions.setExecutors([authUser.id]);
    }, [authUser.id, actions]);

    return {
        isCreationMode: permissions.isCreationMode,
        isEditMode: state.isEditMode,
        canEdit: state.isEditMode && permissions.canEdit,
        defaultUsers: users.map(user => ({ label: formatFullName(user), value: user.id })),
        authorData: [permissions.author?.id, permissions.author && formatFullName(permissions.author)],
        executorData: permissions.executors.map(it => [
            it.id,
            `${formatFullName(it)}${it.status === UserStatus.Archived ? ` (${translate('archive')})` : ''}`,
        ]),
        onExecutorsSelect,
        setOnlyMe,
    };
}
